import React, { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ContentLayout } from 'website/components/Layout/components/ContentLayout';

import {
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Tooltip,
  Box,
  useMediaQuery,
  Button,
} from '@mui/material';
import { IconWrapper } from 'components/Icon';
import { paths } from 'routes';

export const Pricing = () => {
  const { t } = useTranslation();
  const tableRef = useRef(null);
  const rowRef = useRef(null);
  const rowRefMobile = useRef(null);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));

  useEffect(() => {
    const updateWidth = () => {
      if (tableRef.current) {
        const tableWidth = tableRef.current.getBoundingClientRect().width;
        if (rowRef.current) {
          rowRef.current.style.width = `${tableWidth}px`;
        }

        if (rowRefMobile.current) {
          rowRefMobile.current.style.width = `${tableWidth}px`;
        }
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);

    return () => {
      window.removeEventListener('resize', updateWidth);
    };
  }, []);

  const allFeatures = {
    tenders: [
      { id: 't1' },
      { id: 't2' },
      { id: 't3' },
      { id: 't4' },
      { id: 't5' },
      { id: 't6' },
      { id: 't7' },
      { id: 't8' },
    ],
    frameworkAgreements: [
      { id: 'a1' },
      { id: 'a2' },
      { id: 'a3' },
      { id: 'a4' },
      { id: 'a5' },
      { id: 'a6' },
    ],
    dps: [
      { id: 'd1' },
      { id: 'd2' },
      { id: 'd3' },
    ],
    other: [
      { id: 'o1' },
      { id: 'o2' },
      { id: 'o3' },
    ],
  };

  const tiers = [
    {
      title: 'free',
      price: '0',
      currency: 'SEK',
      focused: false,
      features: {
        tenders: ['t1'],
        frameworkAgreements: ['a1'],
        dps: ['d1'],
        other: ['o1'],
      },
    },
    {
      title: 'silver',
      price: '1.995',
      currency: 'SEK',
      focused: true,
      features: {
        tenders: ['t1', 't2', 't3', 't4', 't5', 't6'],
        frameworkAgreements: ['a1', 'a2', 'a3', 'a4'],
        dps: ['d1', 'd2'],
        other: ['o1', 'o2', 'o3'],
      },
    },
    {
      title: 'gold',
      price: '3.995',
      currency: 'SEK',
      focused: false,
      features: {
        tenders: ['t1', 't2', 't3', 't4', 't5', 't6', 't7', 't8'],
        frameworkAgreements: ['a1', 'a2', 'a3', 'a4', 'a5', 'a6'],
        dps: ['d1', 'd2', 'd3'],
        other: ['o1', 'o2', 'o3'],
      },
    },
  ];

  return (
    <ContentLayout>
      <Container
        maxWidth={false}
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          padding: '0px',
          margin: '0 auto',
          my: 4,
          p: {xs: 0, md: 0},
          px: {xs: 4, md: 0},
        }}
      >
      <>
      <TableContainer sx={{width: '100%', tableLayout: "fixed", overflowX: "initial"}}>
        <Table sx={{ width: '100%', tableLayout: "fixed", mt: {xs: 6.2, md: 8.6} }} aria-label={t('website:pricing.comparisonTableLabel')} ref={tableRef}>
          <TableHead>
          <TableRow
            ref={rowRefMobile}
            sx={{
              position: 'fixed',
              top: 114,
              height: 30,
              zIndex: 10,
              bgcolor: '#fff',
              display: { xs: 'flex', md: 'none' },
              width: 1,
            }}
          />
          <TableRow
            ref={rowRefMobile}
            sx={{
              position: 'fixed',
              top: 144,
              zIndex: 10,
              bgcolor: '#fff',
              display: { xs: 'flex', md: 'none' },
            }}
          >
            {tiers.map((tier) => (
              <TableCell key={tier.title} align="center" sx={{ backgroundColor: tier.focused ? 'background.primary' : '#fff', borderBottom: 'none', pb: 1, width: 1, flex: 1 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontSize: '1.125rem',
                    fontWeight: 600,
                    lineHeight: "28px",
                    color: tier.focused ? '#fff' : 'text.custom',
                    flex: 1,
                    textAlign: "center"
                  }}
                >
                  {t(`website:pricing.tiers.${tier.title}`)}
                </Typography>
              </TableCell>
            ))}
          </TableRow>
          <TableRow
            ref={rowRef}
            sx={{
              position: 'fixed',
              top: 114,
              height: 40,
              zIndex: 10,
              bgcolor: '#fff',
              display: { xs: 'none', md: 'flex' },
              width: 1,
            }}
          />
          <TableRow
            ref={rowRef}
            sx={{
              position: 'fixed',
              top: 154,
              flex: 1,
              zIndex: 10,
              display: { xs: 'none', md: 'flex' },
              bgcolor: "#fff",
            }}>
            <TableCell sx={{ borderBottom: 'none', width: 1, flex: 1 }} />
            {tiers.map((tier) => (
              <TableCell key={tier.title} sx={{ backgroundColor: tier.focused ? 'background.primary' : '#fff', borderBottom: 'none', pb: 1, width: 1, flex: 1 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: '2rem',
                    fontWeight: 600,
                    lineHeight: 1.15625,
                    color: tier.focused ? '#fff' : 'text.custom',
                    flex: 1,
                    textAlign: "center"
                  }}
                >
                  {t(`website:pricing.tiers.${tier.title}`)}
                </Typography>
              </TableCell>
            ))}
          </TableRow>
          <TableRow sx={{display: {xs: 'table-row', md: 'table-row'}}}>
            <TableCell sx={{display: {xs: 'none', md: 'table-cell'}}}/>
            {tiers.map((tier) => (
              <TableCell
                key={tier.price}
                align="center"
                sx={{ backgroundColor: tier.focused ? 'background.primary' : '#fff', pt: 1 }}
              >
                <Box>
                  <Box justifyContent={"center"} display={"flex"} flexDirection={{xs: "column", sm: "row"}}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontSize: {xs: '1.125rem', md: '2rem'},
                        fontWeight: 600,
                        lineHeight: 1.15625,
                        color: tier.focused ? "#fff" : "text.custom",
                        textAlign: "center"
                      }}
                    >
                      {tier.price}
                    </Typography>
                    <Typography variant="subtitle1" display={{xs: "none", sm: "block"}} sx={{whiteSpace: 'pre-wrap'}}>
                      {' '}
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontSize: {xs: '1.125rem', md: '2rem'},
                        fontWeight: 600,
                        lineHeight: 1.15625,
                        color: tier.focused ? "#fff" : "text.custom",
                        textAlign: "center"
                      }}
                    >
                      {tier.currency}
                    </Typography>
                  </Box>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontSize: {xs: '0.7rem', md: '1.25rem'},
                        fontWeight: 500,
                        lineHeight: 1,
                        color: tier.focused ? "#fff" : "text.custom",
                        textAlign: "center"
                      }}
                    >
                     {t('website:pricing.perMonthPerUserAnnual')}
                    </Typography>
                  </Box>
              </TableCell>
            ))}
          </TableRow>
          </TableHead>
          <TableBody>
            {Object.keys(allFeatures).map((featureGroup) => (
              <>
                <TableRow sx={{ backgroundColor: 'background.pricing' }}>
                  <TableCell colSpan={isMobile ? tiers.length : tiers.length + 1} sx={{p:1.25}}>
                    <Typography variant="subtitle2" sx={{fontWeight: 600, fontSize: "1.125rem", lineHeight: "28px"}}>{t(`website:pricing.featureGroups.${featureGroup}`)}</Typography>
                  </TableCell>
                </TableRow>
                {allFeatures[featureGroup].map((feature) => (
                  <>
                  <TableRow
                    sx={{
                      display: { xs: 'table-row', md: 'none' },
                    }}
                  >
                    <TableCell colSpan={tiers.length} sx={{borderBottom: 'none'}}>
                    <Box sx={{display: 'flex', alignItems: 'center', fontWeight: 500, lineHeight: "24px"}}>
                        <Tooltip
                          title={t(`website:pricing.buyers.features.${feature.id}.description`, { defaultValue: t('website:pricing.noDescription') })}
                          placement="top-start"
                          enterTouchDelay={1}
                          leaveTouchDelay={5000}
                          slotProps={{
                            popper: {
                              modifiers: [
                                {
                                  name: 'offset',
                                  options: {
                                    offset: [10, -10]
                                  }
                                }
                              ]
                            },
                            tooltip: {
                              sx: {
                                color: "#fff",
                                backgroundColor: "background.primary",
                                maxWidth: '200px',
                                border: "0.806px solid background.primary",
                                borderRadius: '19.72px',
                                padding: 2,
                                fontSize: '1rem',
                                fontWeight: 400,
                                lineHeight: '1.487',
                                letterSpacing: "-0.08px",
                                textAlign: 'left',
                              },
                            },
                          }}
                        >
                          <Box sx={{mr:0.5}}>
                            <IconWrapper
                              icon="info"
                              sx={({palette}) => ({
                                svg: {
                                  stroke: palette.background.primary,
                                  strokeWidth:2,
                                  height: 16,
                                  width: 16,
                                  mt: -0.3,
                                }
                              })}
                            />
                          </Box>
                        </Tooltip>
                        {t(`website:pricing.buyers.features.${feature.id}.name`)}
                      </Box>
                    </TableCell>
                  </TableRow>
                  <TableRow
                    key={feature}
                    sx={{ 
                      borderBottom: '2px solid #F3F3F3',
                      px: 1,
                      width: 1,
                      height: 40,
                    }}
                  >
                    <TableCell component="th" scope="row" sx={{p:1.25, display: {xs: "none", md: "table-cell"}}}>
                      <Box sx={{display: 'flex', alignItems: 'center', fontWeight: 500, lineHeight: "24px"}}>
                        <Tooltip
                          title={t(`website:pricing.buyers.features.${feature.id}.description`, { defaultValue: t('website:pricing.noDescription') })}
                          placement="top-start"
                          slotProps={{
                            popper: { 
                              modifiers: [
                                {
                                  name: 'offset', 
                                  options: { 
                                    offset: [10, -10] 
                                  } 
                                }
                              ]
                            },
                            tooltip: {
                              sx: {
                                color: "#fff",
                                backgroundColor: "background.primary",
                                maxWidth: '200px',
                                border: "0.806px solid background.primary",
                                borderRadius: '19.72px',
                                padding: 2,
                                fontSize: '1rem',
                                fontWeight: 400,
                                lineHeight: '1.487',
                                letterSpacing: "-0.08px",
                                textAlign: 'left',
                              },
                            },
                          }}
                        >
                          <Box sx={{mr:0.5}}>
                            <IconWrapper
                              icon="info"
                              sx={({palette}) => ({
                                svg: {
                                  stroke: palette.background.primary,
                                  strokeWidth:2,
                                  height: 16,
                                  width: 16,
                                  mt: -0.3,
                                }
                              })}
                            />
                          </Box>
                        </Tooltip>
                        {t(`website:pricing.buyers.features.${feature.id}.name`)}
                      </Box>
                    </TableCell>
                    {tiers.map((tier, index) => (
                      <TableCell key={tier.title + index} align="center"  sx={{p:1.25}}>
                        {tier.features[featureGroup]?.includes(feature.id) && (
                          <IconWrapper
                          icon="confirm"
                          sx={({palette}) => ({
                            svg: {
                              stroke: "#2A6672",
                              strokeWidth:2,
                              height: 16,
                              width: 16,
                            }
                          })}
                        />
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                  </>
                ))}
              </>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Typography variant='body2' sx={{fontSize: "1rem", fontWeight:500, lineHeight: "28px", mt: 2}}>{t('website:pricing.annualRebateNote')}</Typography>
      </>
    </Container>
    <Button
      size="medium"
      variant="contained"
      component="a"
      href={paths.auth.buyersRegister}
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        backgroundColor: "#62A7B7",
        '&:hover': {
          backgroundColor: "#62A7B7",
        },
        minWidth: 200,
        height: 48,
        whiteSpace: 'nowrap',
        fontSize: "1rem",
        fontWeight: "700",
        color: "#fff",
        lineHeight: 1.5,
        display: {xs: "flex", md: "none"},
        mb: 3,
        mt: -9,
      }}
    >
      {t('website:pricing.signUpFree')}
    </Button>
    </ContentLayout>
  );
};